// Column model shared by the settings dialog, the messages grid, and URL-state
// persistence. Kept in its own leaf module (no component imports) so the value
// exports below can be used at module-eval time without tripping the
// FixSettings <-> FixMessagesGridSettings import cycle.

export enum GroupRenderStrategy {
  FIRST = "FIRST",
  LAST = "LAST",
  ALL = "ALL",
  COUNT = "COUNT",
}

/**
 * What a column renders. `TAG` is the original behavior — pull a single FIX
 * tag's value from each message (this also covers the default Sender/Target
 * columns, which are just tags 49/56). The remaining kinds back the built-in
 * default columns that render derived values (message time, message type,
 * summary) rather than a single configurable tag.
 */
export enum ColumnType {
  TAG = "TAG",
  TIME = "TIME",
  MESSAGE_TYPE = "MESSAGE_TYPE",
  DETAIL = "DETAIL",
}

export interface ColumnSpec {
  fixTag: number;
  label: string;
  useEnum: boolean;
  // What the column renders. Absent on legacy persisted state, where every
  // column was a plain FIX-tag column — treat `undefined` as `ColumnType.TAG`.
  type?: ColumnType;
  highlightDuplicates?: boolean;
  groupRenderStrategy?: GroupRenderStrategy;
  // Optional filter to limit values rendered from a group entry by exact match
  // When provided, the renderer should only consider members where the given
  // fixTag has the exact string value specified. Nullable to allow skipping.
  groupEntryFilter?: { fixTag: number; value: string } | null;
}

/**
 * The columns shown when the user hasn't customized anything. These are now
 * ordinary, editable rows in the "Change columns" dialog rather than hardcoded
 * grid columns, so they can be renamed, reordered, retyped, or removed. The
 * `fixTag` values are the FIX tags these derived columns are based on; they are
 * informational for the non-TAG types (rendering keys off `type`).
 */
export const DEFAULT_COLUMNS: ColumnSpec[] = [
  { type: ColumnType.TIME, fixTag: 52, label: "Time", useEnum: false },
  { type: ColumnType.TAG, fixTag: 49, label: "Sender", useEnum: false },
  { type: ColumnType.TAG, fixTag: 56, label: "Target", useEnum: false },
  { type: ColumnType.MESSAGE_TYPE, fixTag: 35, label: "Type", useEnum: false },
  { type: ColumnType.DETAIL, fixTag: 0, label: "Detail", useEnum: false },
];

/**
 * Bumped when the meaning of persisted `columns` changes. Version-less (legacy)
 * state stored only the user's *custom* columns and relied on hardcoded default
 * columns; on load we prepend {@link DEFAULT_COLUMNS} to recover the old view.
 * Version 2 persists the full column list, defaults included.
 */
export const COLUMNS_VERSION = 2;

const columnsEqual = (a: ColumnSpec, b: ColumnSpec): boolean =>
  (a.type ?? ColumnType.TAG) === (b.type ?? ColumnType.TAG) &&
  a.fixTag === b.fixTag &&
  a.label === b.label &&
  a.useEnum === b.useEnum &&
  Boolean(a.highlightDuplicates) === Boolean(b.highlightDuplicates) &&
  (a.groupRenderStrategy ?? GroupRenderStrategy.FIRST) ===
    (b.groupRenderStrategy ?? GroupRenderStrategy.FIRST) &&
  JSON.stringify(a.groupEntryFilter ?? null) ===
    JSON.stringify(b.groupEntryFilter ?? null);

/** True when `columns` is exactly the untouched default set (any order change,
 * rename, retype, addition, or removal makes it non-default). */
export const isDefaultColumns = (columns: ColumnSpec[]): boolean =>
  columns.length === DEFAULT_COLUMNS.length &&
  columns.every((c, i) => columnsEqual(c, DEFAULT_COLUMNS[i]));

/** Restore the full column list from persisted state of any version. */
export const migrateColumns = (
  columns: ColumnSpec[],
  version: number | undefined,
): ColumnSpec[] =>
  version === COLUMNS_VERSION ? columns : [...DEFAULT_COLUMNS, ...columns];
